// Shared wire format for the Lumen element control panel.
//
// The cache page iframe is cross-origin (content.lumen-research.com vs the dashboard), so the
// dashboard cannot touch its DOM. Everything goes over postMessage to a small listener inside
// the cache page - the same shape as the existing preview-ad-tags handshake.
//
// The listener that has to live in the cache page is public/lumen-panel-listener.js. It is
// kept out of src/ deliberately so it can never be pulled into the dashboard bundle.
export const PANEL_PREFIX = 'lumen-panel:';

export const MESSAGES = {
    // dashboard -> cache page
    PING: 'PING',
    LIST_ELEMENTS: 'LIST_ELEMENTS',
    APPLY_OVERRIDES: 'APPLY_OVERRIDES',
    HIGHLIGHT: 'HIGHLIGHT',
    // cache page -> dashboard
    READY: 'READY',
    ELEMENTS: 'ELEMENTS',
    APPLIED: 'APPLIED',
};

// Logs both directions to the dashboard console, so a click in the panel can be traced.
// Pair it with the cache page's debug listener: click a control and you should see a
// "-> sending" line here and a matching "received" line in the cache page.
//
//   only "-> sending"        the message left the dashboard but the page never got it
//   neither line             the panel is not firing at all
//   both, then "<- received" the whole round trip works
//
// SET THIS TO false ONCE THE PANEL IS WORKING.
const DEBUG = true;
const TAG = '[lumen-panel]';

// Only elements whose id or class begins with this are offered for editing.
export const LUMEN_PREFIX = 'lumen';

export function encode(name, value) {
    return PANEL_PREFIX + JSON.stringify({ name, value });
}

export function decode(data) {
    if (typeof data !== 'string' || data.indexOf(PANEL_PREFIX) !== 0) {
        return null;
    }

    try {
        // sliced by the prefix length rather than a hardcoded number - the existing
        // preview-ad-tags listener hardcodes 21 for a 16 character prefix
        return JSON.parse(data.slice(PANEL_PREFIX.length));
    } catch (error) {
        return null;
    }
}

export function sendToFrame(frame, name, value) {
    // contentWindow is readable cross-origin even though contentDocument is not
    if (!frame || !frame.contentWindow) {
        if (DEBUG) {
            console.log(TAG, 'CANNOT SEND', name, '- the iframe is not available yet');
        }

        return false;
    }

    if (DEBUG) {
        console.log(TAG, '-> sending', name, value === undefined ? '' : value);
    }


    frame.contentWindow.postMessage(encode(name, value), '*');

    return true;
}

export function subscribe(handler) {
    function onMessage(event) {
        const message = decode(event.data);

        if (!message) {
            return;
        }

        if (DEBUG) {
            console.log(TAG, '<- received', message.name, 'from', event.origin, message.value);
        }

        handler(message);
    }

    window.addEventListener('message', onMessage);

    return () => window.removeEventListener('message', onMessage);
}

// The override shape, documented in one place because both sides depend on it:
//
//   {
//     hidden: false,          // display: none
//     offsetX: -16,           // translateX in px, negative moves left
//     color: '#ffffff',       // text colour
//     backgroundColor: '#000',
//     position: 'fixed',      // '' leaves the page's own value alone
//   }
export const EMPTY_OVERRIDE = {
    hidden: false,
    offsetX: 0,
    color: '',
    backgroundColor: '',
    position: '',
};

export function hasChanges(override) {
    if (!override) {
        return false;
    }

    return Boolean(
        override.hidden ||
        override.offsetX ||
        override.color ||
        override.backgroundColor ||
        override.position
    );
}

/*
 * Just the properties of one override that differ from EMPTY_OVERRIDE, or null if none do.
 *
 * The panel always holds a complete override per element - selecting an element and nudging
 * it left still carries hidden:false, color:'', position:'' alongside the offsetX that
 * actually changed. That is what the live preview wants, but saving those defaults records
 * "no change" as though it were a change.
 *
 * Compares against the override's own keys rather than EMPTY_OVERRIDE's, so a property added
 * to the panel later is kept rather than silently dropped.
 */
export function changedProperties(override) {
    if (!override) {
        return null;
    }

    const changed = {};

    Object.keys(override).forEach((property) => {
        if (override[property] !== EMPTY_OVERRIDE[property]) {
            changed[property] = override[property];
        }
    });

    return Object.keys(changed).length ? changed : null;
}

/*
 * Turns one saved override into the CSS declarations that express it, as [property, value]
 * pairs ready for element.style.setProperty().
 *
 * The stored property names are this panel's own vocabulary, NOT CSS:
 *
 *   hidden          ->  display: none          (not a CSS property)
 *   offsetX         ->  transform: translateX(Npx)   (not a CSS property)
 *   backgroundColor ->  background-color       (setProperty needs the hyphenated CSS name;
 *                                               the camelCase form is the JS accessor name
 *                                               and setProperty silently ignores it)
 *   color           ->  color                  (JS and CSS names happen to match)
 *   position        ->  position               (likewise)
 *
 * That last pair is why colour and position appear to work while background colour does
 * nothing - the two names that survive a naive setProperty are the two that need no
 * translation.
 */
export function toCssDeclarations(override) {
    if (!override) {
        return [];
    }

    // hidden wins outright - nothing else about the element is visible
    if (override.hidden) {
        return [['display', 'none']];
    }

    const declarations = [];

    if (override.offsetX) {
        declarations.push(['transform', `translateX(${override.offsetX}px)`]);
    }

    if (override.color) {
        declarations.push(['color', override.color]);
    }

    if (override.backgroundColor) {
        declarations.push(['background-color', override.backgroundColor]);
    }

    if (override.position) {
        declarations.push(['position', override.position]);
    }

    // anything the panel gains later passes through with its name converted, so a new
    // property works here without a second edit
    const known = ['hidden', 'offsetX', 'color', 'backgroundColor', 'position'];

    Object.keys(override).forEach((property) => {
        if (known.includes(property) || !override[property]) {
            return;
        }

        declarations.push([
            property.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`),
            override[property],
        ]);
    });

    return declarations;
}

// The same, across every element: elements left at their defaults drop out entirely.
export function changedOverrides(overrides) {
    const changed = {};

    Object.keys(overrides || {}).forEach((key) => {
        const properties = changedProperties(overrides[key]);

        if (properties) {
            changed[key] = properties;
        }
    });

    return changed;
}

/*
 * What gets SAVED: only the altered properties, expressed as real CSS.
 *
 *   panel state                                  saved
 *   { hidden: false, offsetX: 56,          ->    { transform: 'translateX(56px)',
 *     color: '', backgroundColor: '#726e6f',       'background-color': '#726e6f',
 *     position: 'absolute' }                       position: 'absolute' }
 *
 * The conversion has to happen here rather than when the styling is applied, because
 * whatever reads this back hands the pairs straight to element.style.setProperty(), and
 * setProperty only understands CSS names. Storing `backgroundColor` means every consumer
 * has to know to translate it, and any that forgets drops the declaration silently -
 * setProperty ignores names it does not recognise without raising anything.
 */
/*
 * The key as it is SAVED.
 *
 * The panel and the cache page identify an element as "id:lumen-header" or
 * "class:lumen-slot#0" - the prefix says how the element was found. That colon makes the
 * key awkward to iterate over once it is stored, so it is dropped on the way out:
 *
 *   id:lumen-header                  ->  lumen-header
 *   id:lumen-cachepage-1/A-container ->  lumen-cachepage-1/A-container
 *   class:lumen-slot#0               ->  lumen-slot#0
 *
 * Both prefixes go, not just "id:" - a class-keyed element carries the same colon and
 * would break the same iteration. The injection script puts the prefix back when it
 * resolves the element, so nothing is lost.
 */
export function toSavedKey(key) {
    return String(key).replace(/^(id|class):/, '');
}

export function toCssOverrides(overrides) {
    const saved = {};

    Object.keys(overrides || {}).forEach((key) => {
        const declarations = toCssDeclarations(changedProperties(overrides[key]));

        if (!declarations.length) {
            return;
        }

        saved[toSavedKey(key)] = declarations.reduce((properties, [property, value]) => {
            properties[property] = value;

            return properties;
        }, {});
    });

    return saved;
}
